import React, { useEffect, useState, lazy, Suspense } from 'react'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import { headingListIcon } from '@toasttab/shared/src/utils/Contentful/styledList'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as IconClose } from '@toasttab/shared/src/assets/svg/icon-close.svg'
import { ContentfulShowcaseForm } from '@toasttab/shared/src/interfaces/ContentfulShowcaseForm.d'

const MainFormContentful = lazy(
  () =>
    import(
      '@toasttab/shared/src/components/molecules/Contentful/Form/MainFormContentful'
    )
)

// default to 5 seconds if not overridden by an integration test or unit test
const delayMillisecondsDefault = 5000

// Cookie names used by the exit intent modal
const cookieNames = {
  exitIntentTriggered: 'exitIntentTriggered',
  toastDemoForm: 'toastDemoForm',
  toastResourceForm: 'toastResourceForm'
}

// Local storage keys used by the exit intent modal
const localStorageKeys = {
  // Allows integration or unit tests to force set the delay milliseconds to not have to wait for the default 5 seconds
  exitIntentDelayMillisecondsOverride: 'exitIntentDelayMillisecondsOverride'
}

interface ExitIntentProps {
  showcaseForm: ContentfulShowcaseForm
}

const ExitIntent = React.memo(({ showcaseForm }: ExitIntentProps) => {
  const [showModal, setShowModal] = useState(false)

  // Pull the heading list from the form so it can be displayed on the left side of the modal
  // and set the original to an empty array so it won't show above the form
  const headingList = Array.isArray(showcaseForm.form.headingList)
    ? showcaseForm.form.headingList.slice()
    : []

  // Make a copy with an empty array instead of replacing
  const formWithEmptyHeadingList = {
    ...showcaseForm.form,
    headingList: [] as any[]
  }

  const checkExitIntentEnabled = () => {
    // if this form has a formCookie name, check cookies to see if this form has been filled out
    if (
      showcaseForm.form.formCookie &&
      !!_helpers.getCookie(showcaseForm.form.formCookie)
    ) {
      // console.log('exit intent not enabled: user already filled out this form')
      return false
    }

    // check cookies to see if a demo form has been filled out
    if (!!_helpers.getCookie(cookieNames.toastDemoForm)) {
      // console.log('exit intent not enabled: user already filled out a demo form')
      return false
    }

    // check cookies to see if a resource form has been filled out
    // this cookie name happens to also be used on international demo forms
    if (!!_helpers.getCookie(cookieNames.toastResourceForm)) {
      // console.log('exit intent not enabled: user already filled out a resource form or an international demo form')
      return false
    }

    // check cookies to see if the exit intent has already been displayed to the user during this browser session
    if (!!_helpers.getCookie(cookieNames.exitIntentTriggered)) {
      // console.log('exit intent not enabled: already displayed to user this session')
      return false
    }

    // console.log('exit intent enabled')
    return true
  }

  // Set up the timer and mouse leave event
  useEffect(() => {
    const handleMouseLeave = (event: MouseEvent) => {
      if (event.clientY < 0) {
        setShowModal(true)

        // Set the session cookie to indicate that the user has seen the exit intent
        // It should expire when the session ends
        _helpers.createCookie(cookieNames.exitIntentTriggered, '1', 0, '/')

        // Remove the event listener so it doesn't get triggered again
        document.body.removeEventListener('mouseleave', handleMouseLeave)
      }
    }

    // Check if a delay milliseconds override is set in local storage by an integration test or unit test
    // otherwise use the default
    const delayMillisecondsOverride = window.localStorage.getItem(
      localStorageKeys.exitIntentDelayMillisecondsOverride
    )
    const delayMilliseconds = delayMillisecondsOverride
      ? parseInt(delayMillisecondsOverride)
      : delayMillisecondsDefault

    const timeoutId = window.setTimeout(() => {
      if (checkExitIntentEnabled()) {
        document.body.addEventListener('mouseleave', handleMouseLeave)
      }
    }, delayMilliseconds)

    return () => {
      clearTimeout(timeoutId)
      document.body.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [])

  if (!showModal) {
    return null
  }

  return (
    <div
      className='fixed inset-0 bg-black/30 flex items-center justify-center z-[101] top-0 left-0 right-0 bottom-0'
      data-testid='ExitIntentModal-backdrop'
      onClick={() => setShowModal(false)}
    >
      <div
        className='rounded-lg max-w-[936px] min-h-[544px] w-full h-auto mx-4 my-4 flex overflow-hidden z-[102]'
        data-testid='ExitIntentModal-container'
        onClick={(e) => e.stopPropagation()}
      >
        {/* Left half - bg-neutral-dark background */}
        <div className='flex-1 bg-neutral-dark p-8 flex flex-col'>
          <h3 className='mb-4' data-testid='ExitIntentModal-heading'>
            {showcaseForm.heading}
          </h3>
          {headingList && headingList.length > 0 ? (
            <ul
              className='list-none mb-10'
              data-testid='ExitIntentModal-headingList'
            >
              {headingList.map((item: any, i: number) => (
                <li
                  className='flex text-base text-secondary mb-1 last:mb-none'
                  key={i}
                >
                  <span
                    className={`mr-2 h-4 w-4 before:mr-1 before:h-4 before:w-4 before:inline-block ${headingListIcon(
                      item.key
                    )}`}
                  />
                  {item.value.content[0].content[0].value}
                </li>
              ))}
            </ul>
          ) : null}
          {showcaseForm.asset?.file?.url ? (
            <picture
              className='mt-auto mb-5'
              data-testid='ExitIntentModal-image'
            >
              <source
                srcSet={showcaseForm.asset.file.url + '?fm=avif'}
                type='image/avif'
              />
              <img
                alt={showcaseForm.asset.title}
                className='lazyload mb-4'
                src={showcaseForm.asset.file.url + '?fm=png'}
              />
            </picture>
          ) : null}
        </div>

        {/* Right half - White background */}
        <div className='flex-1 bg-white px-8 pb-8 pt-6 [&>div>form>div>button]:!w-auto'>
          <div className='flex justify-end mb-6'>
            <IconClose
              className='w-6 h-6 cursor-pointer'
              data-testid='ExitIntentModal-closeIcon'
              fill='#666666'
              onClick={() => setShowModal(false)}
            />
          </div>
          <Suspense fallback={<div style={{ height: 580 }} />}>
            <MainFormContentful
              {...(formWithEmptyHeadingList as any)}
              componentContext='ContentfulMdsShowcaseForm'
              fieldIdPrefix='ContentfulHomepageHeroDemoRequest'
              initExtraValues={{}}
            />
          </Suspense>
        </div>
      </div>
    </div>
  )
})

export default ExitIntent
